import React, { useEffect, useState } from 'react';
import { IRepasseParceiro } from '../../../../../../../../../interfaces/IBaseDeCalculo';
import styles from './operacaoRepasseLista.module.scss';
import InformacaoBasica from '../../../../../../../../../components/informacaoBasica/informacaoBasica';
import { BrButton } from '@govbr-ds/react-components';
import {
  DocumentoVinculadoDetalhe,
  EDocumentoVinculado,
  ETipoVinculo,
  ETransportador,
} from '../../../../../../../../../enums/ERepasseParceiro';
import NCMViewContainerList from '../baseDeCalculoFormItemNCM/components/ncmViewContainerList/ncmViewContainerList';
import { Button, Modal } from 'react-dsgov';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { formatarCNPJOrCpf } from '../../../../../../../../../utils/formatCNPJOrCPF';
import CustomConfirmer from '../../../../../../../../../components/customConfirmer/customConfirmer';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface ITitularArrendatarioOperacaoRepasseListaProps {
  repassesParceiros: IRepasseParceiro[];
  setRepassesParceiros: React.Dispatch<React.SetStateAction<IRepasseParceiro[]>>;
  repasseParceiroEdicao: IRepasseParceiro | undefined;
  repasseParceiroCadastro: IRepasseParceiro | undefined;
  setRepasseParceiroEdicao: React.Dispatch<React.SetStateAction<IRepasseParceiro | undefined>>;
  headers: Array<{ field: string; label: string; classe?: string }>;
}

const OperacaoRepasseLista: React.FC<ITitularArrendatarioOperacaoRepasseListaProps> = ({
  repassesParceiros,
  headers,
  setRepassesParceiros,
  repasseParceiroEdicao,
  setRepasseParceiroEdicao,
  repasseParceiroCadastro,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [idRepasseADeletar, setIdRepasseADeletar] = useState<number>();
  const [exclusaoConfirmada, setExclusaoConfirmada] = useState(false);
  const { deleteRepasseParceiroPLG } = useBackEndRequests();

  const { data: deleteRepasseData } = useQuery<string, IErrorWithSentryId>({
    queryKey: ['deleteRepassePLG', idRepasseADeletar],
    queryFn: async () => {
      await deleteRepasseParceiroPLG(idRepasseADeletar!);
      return 'Repasse excluído com sucesso';
    },
    enabled: !!idRepasseADeletar && exclusaoConfirmada,
  });

  useEffect(() => {
    if (deleteRepasseData) {
      setExclusaoConfirmada(false);
      setIdRepasseADeletar(undefined);
      setRepassesParceiros((repassesParceiros) =>
        repassesParceiros.filter((repasseParceiro) => repasseParceiro.id !== idRepasseADeletar)
      );
    }
  }, [deleteRepasseData]);

  const abrirFecharModal = () => {
    setOpenModal(!openModal);
  };

  const confirmarExclusao = async () => {
    setExclusaoConfirmada(true);
    abrirFecharModal();
  };

  const excluirRepasseParceiro = (idRepasseParceiro: number) => {
    abrirFecharModal();
    setIdRepasseADeletar(idRepasseParceiro);
  };

  const editarRepasseParceiro = (repasseParceiro: IRepasseParceiro) => {
    setRepasseParceiroEdicao(repasseParceiro);
  };

  return (
    <div className={styles.containerVendaDireta}>
      {repassesParceiros.map(
        (repasseParceiro, index) =>
          repasseParceiro.id !== repasseParceiroEdicao?.id &&
          repasseParceiro.id !== repasseParceiroCadastro?.id && (
            <div key={index} className={styles.containerBaseDeCalculo}>
              <div key={index} className={styles.containerDadosBasicos}>
                {headers.map((header, index) => (
                  <InformacaoBasica
                    key={index}
                    aria-label={`${header.label}: ${(repasseParceiro as any)[header.field]}`}
                    titulo={header.label}
                    conteudo={(() => {
                      if (header.field.includes('cpfCnpj') && repasseParceiro[header.field as keyof IRepasseParceiro]) {
                        return formatarCNPJOrCpf((repasseParceiro as any)[header.field]);
                      }
                      if (header.field === 'tipoVinculo') {
                        return ETipoVinculo[repasseParceiro.tipoVinculo as keyof typeof ETipoVinculo];
                      }

                      if (header.field === 'transportador') {
                        return ETransportador[repasseParceiro.transportador as keyof typeof ETransportador];
                      }

                      return (repasseParceiro as any)[header.field];
                    })()}
                  />
                ))}
              </div>
              {repasseParceiro.documentosVinculados.length > 0 && (
                <div className={styles.documentosVinculados}>
                  <strong>Documentos Vinculados:</strong>
                  {repasseParceiro.documentosVinculados.map((documentoVinculado, index) => (
                    <li key={index}>
                      {
                        DocumentoVinculadoDetalhe[
                          EDocumentoVinculado[documentoVinculado as keyof typeof EDocumentoVinculado]
                        ]?.label
                      }
                      {documentoVinculado === 'OUTROS' ? ` : ${repasseParceiro.documentosVinculadosDescricao}` : ''}
                    </li>
                  ))}
                </div>
              )}
              {
                <NCMViewContainerList
                  idRepasseParceiro={repasseParceiro.id}
                  refreshQuery={true}
                  setRefreshQuery={() => {}}
                  hideAction={true}
                />
              }
              <div className={styles.botoes}>
                <BrButton
                  secondary
                  circle
                  onClick={() => excluirRepasseParceiro(repasseParceiro.id || 0)}
                  title='Excluir'
                >
                  <i className={`fas fa-trash-alt fa-md  ${styles.columnIcon}`} />
                </BrButton>
                <BrButton secondary circle onClick={() => editarRepasseParceiro(repasseParceiro)} title='Editar'>
                  <i className={`fas fa-edit fa-md  ${styles.columnIcon}`} />
                </BrButton>
                <Modal title='Excluir Repasse Parceiro PLG' useScrim modalOpened={openModal}>
                  <Modal.Body>Deseja excluir o Repasse selecionado?</Modal.Body>
                  <Modal.Footer justify-content='end'>
                    <Button secondary small m={2} onClick={abrirFecharModal}>
                      Cancelar
                    </Button>
                    <CustomConfirmer asyncCall={() => confirmarExclusao()}>Confirmar</CustomConfirmer>
                  </Modal.Footer>
                </Modal>
              </div>
            </div>
          )
      )}
    </div>
  );
};

export default OperacaoRepasseLista;
